import React, { useState } from 'react';
import {
    CircularProgress,
    Box,
    Button,
    Tabs,
    Tab,
    Typography
} from '@mui/material';
import OptionSelector from './OptionSelector';
import { DisDatType } from '../Const';
import { executeFetchAsync } from '../../Util';
import { config } from '../../Const';

export default function OptionFactBank({
    disDatSelection,
    onSelectionChange,
    onSelectFact
}) {

    const {
        type = '',
        selectedArtistOrGroup = null,
        entity1 = null,
        entity2 = null,
        customInstructions = ''
    } = disDatSelection || {};

    const [isLoading, setIsLoading] = useState(false);
    const [optionFacts, setOptionFacts] = useState([]);
    const [selectedEntityTab, setSelectedEntityTab] = useState(0);
    const [innerTabs, setInnerTabs] = useState({ 0: 0, 1: 0 });
    const [aiFactsByEntityIdx, setAiFactsByEntityIdx] = useState({ 0: [], 1: [] });
    const [isAiFactsLoading, setIsAiFactsLoading] = useState(false);
    const AI_FACTS_TAB_LABEL = 'AI Facts';
    const AI_FACTS_COUNT = 10;

    const optionFactCategoryToDisplayCategory = {
        PERSONAL_DETAILS: "General",
        GENERAL: "General",
        QUOTES: "Quotes",
        RELATIONSHIPS: "Relationships",
        BAND_MEMBERSHIP: "Band Origin & Membership",
        ROLE_IN_BAND: "Band Origin & Membership",
        BAND_INCEPTION: "Band Origin & Membership",
        SONGS_IN_BAND: "Band Songs",
        ROLE_IN_SONG: "Band Songs",
        ARTIST_SONG: type === DisDatType.MUSICAL_GROUP_MEMBER_VS_MEMBER ? "Solo Songs" : "Songs",
        SONG_AUDIO: "Songs",
        SOLO_SONGS: "Solo Songs",
        SOLO_ALBUMS: "Albums",
        ARTIST_ALBUM: type === DisDatType.MUSICAL_GROUP_MEMBER_VS_MEMBER ? "Solo Albums" : "Albums",
        ALBUM_SONGS: "Albums",
        ALBUM_RELEASE_YEAR: "Albums",
        ALBUM_BILLBOARD_CHARTS: "Accolades",
        ALBUM_UK_CHARTS: 'Accolades',
        GRAMMY_AWARDS: "Accolades",
        ROCK_HALL_OF_FAME: "Accolades",
        SONG_BILLBOARD_CHARTS: "Accolades",
        SONG_UK_CHARTS: 'Accolades',
        LIVE_CONCERTS: "Live Concerts",
        SNL_GUEST: "In TV/Movies",
        MOVIE_ROLES: "In TV/Movies"
    };

    const handleSelect = ({ type, selectedArtistOrGroup, entity1, entity2, customInstructions }) => {
        setOptionFacts([]);
        setInnerTabs({ 0: 0, 1: 0 });

        // AI ONLY: reset AI facts when selection changes
        setAiFactsByEntityIdx({ 0: [], 1: [] });

        onSelectionChange({ type, selectedArtistOrGroup, entity1, entity2, customInstructions });
    };

    const onGenerateOptionFacts = async () => {

        const payload = buildDisDatPayload({
            type,
            selectedArtistOrGroup,
            entity1,
            entity2
        });

        try {
            setIsLoading(true);

            // =========================
            // Data facts
            // =========================

            if (type !== DisDatType.CUSTOM) {

                const question = await executeFetchAsync(
                    `${config.SERVER_URL}/api/v1/admin/disdat/generate`,
                    'post',
                    { 'Content-Type': 'application/json' },
                    JSON.stringify(payload)
                );
                setOptionFacts(question.criteria.disDatOptionFactQVariableList || []);
            }

            // =========================
            // AI facts
            // =========================

            try {
                setIsAiFactsLoading(true);

                const aiPayload = {
                    type,
                    option1QVariable: entity1,
                    option2QVariable: entity2,
                    selectedArtistOrGroup,
                    customInstructions,
                    count: AI_FACTS_COUNT
                };

                const res = await executeFetchAsync(
                    `${config.SERVER_URL}/api/v1/admin/AI/generate-disdat-facts`,
                    'post',
                    { 'Content-Type': 'application/json' },
                    JSON.stringify(aiPayload)
                );

                // Use JSON exactly as server returns (no fallbacks)
                const parsed = JSON.parse(res);
                const byIdx = { 0: [], 1: [] };

                // Albums are identified by MusicBrainzId
                if (type === DisDatType.MUSICAL_GROUP_ALBUM_VS_ALBUM || type === DisDatType.MUSICIAN_ALBUM_VS_ALBUM) {

                    const qe1Id = entity1.quizzlerObject.idMapping.MUSICBRAINZ;
                    const qe2Id = entity2.quizzlerObject.idMapping.MUSICBRAINZ;

                    parsed.facts_structured.forEach((f) => {
                        if (f.entityMbid === qe1Id) byIdx[0].push(f);
                        if (f.entityMbid === qe2Id) byIdx[1].push(f);
                    });


                    // The rest is identified by wikidataId
                } else if (type === DisDatType.CUSTOM) {


                    const qe1Id = entity1.quizzlerObject.idMapping.QUIZMYDAY;
                    const qe2Id = entity2.quizzlerObject.idMapping.QUIZMYDAY;

                    parsed.facts_structured.forEach((f) => {
                        if (f.entityQmdId === qe1Id) byIdx[0].push(f);
                        if (f.entityQmdId === qe2Id) byIdx[1].push(f);
                    });

                }


                else {

                    const qe1Id = entity1.quizzlerObject.idMapping.WIKIDATA;
                    const qe2Id = entity2.quizzlerObject.idMapping.WIKIDATA;

                    parsed.facts_structured.forEach((f) => {
                        if (f.entityWikidataId === qe1Id) byIdx[0].push(f);
                        if (f.entityWikidataId === qe2Id) byIdx[1].push(f);
                    });

                }



                setAiFactsByEntityIdx(byIdx);

            } catch (e) {
                console.error("Failed to load AI facts:", e);
            } finally {
                setIsAiFactsLoading(false);
            }


        } catch (error) {
            console.error("Failed to generate option facts:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const buildDisDatPayload = () => {
        const isAlbumType = type === DisDatType.MUSICIAN_ALBUM_VS_ALBUM || type === DisDatType.MUSICAL_GROUP_ALBUM_VS_ALBUM;
        const isMemberType = type === DisDatType.MUSICAL_GROUP_MEMBER_VS_MEMBER;
        const isAlbumOrMember = isAlbumType || isMemberType;

        const disDatType =
            isAlbumType && selectedArtistOrGroup?.quizzlerObject?.type === 'MUSICAL_ARTIST'
                ? DisDatType.MUSICIAN_ALBUM_VS_ALBUM
                : type;

        const payload = {
            type: 'DisDatQuestionParams',
            disDatType,
            option1QVariable: entity1 || null,
            option2QVariable: entity2 || null
        };

        if (isAlbumOrMember && selectedArtistOrGroup?.idMapping?.WIKIDATA) {
            payload.quizzlerEntity = {
                idMapping: {
                    WIKIDATA: selectedArtistOrGroup.idMapping.WIKIDATA
                }
            };
        }

        return payload;
    };

    const handleEntityTabChange = (event, newValue) => {
        setSelectedEntityTab(newValue);
    };

    const handleInnerTabChange = (entityIndex, newValue) => {
        setInnerTabs(prev => ({ ...prev, [entityIndex]: newValue }));
    };

    return (
        <Box sx={{ height: '100%', pl: '10px', pr: '20px', overflowY: 'auto' }}>
            <OptionSelector
                type={type}
                selectedArtistOrGroup={selectedArtistOrGroup}
                entity1={entity1}
                entity2={entity2}
                customInstructions={customInstructions}
                onSelect={handleSelect}
            />

            <Box sx={{ mt: 2 }}>
                {(isLoading || isAiFactsLoading) && (
                    <Box sx={{ padding: '10px' }}>
                        <CircularProgress />
                    </Box>
                )}


                <Button onClick={onGenerateOptionFacts} variant="contained" sx={{ mt: 2 }}>
                    Generate
                </Button>
            </Box>

            <Box sx={{ mt: 4 }}>
                {(optionFacts.length > 0 || aiFactsByEntityIdx[0].length > 0 || aiFactsByEntityIdx[1].length > 0) && (
                    <>
                        <Tabs
                            value={selectedEntityTab}
                            variant="fullWidth"
                            onChange={handleEntityTabChange}
                            TabIndicatorProps={{ style: { display: 'none' } }}
                        >
                            <Tab
                                label={entity1?.label || "Option 1"}
                                sx={{
                                    bgcolor: selectedEntityTab === 0 ? 'primary.main' : 'grey.200',
                                    color: selectedEntityTab === 0 ? 'white !important' : 'black',
                                    borderRadius: 1,
                                    mx: 0.5,
                                }}
                            />
                            <Tab
                                label={entity2?.label || "Option 2"}
                                sx={{
                                    bgcolor: selectedEntityTab === 1 ? 'primary.main' : 'grey.200',
                                    color: selectedEntityTab === 1 ? 'white !important' : 'black',
                                    borderRadius: 1,
                                    mx: 0.5,
                                }}
                            />
                        </Tabs>

                        {[entity1, entity2].map((entity, entityIdx) => {

                            if (selectedEntityTab !== entityIdx) return null;

                            const entityFacts = optionFacts.filter((of) => {
                                const ofIdMapping = of.ofqo?.correctOption?.qv?.quizzlerObject?.idMapping;
                                const entityIdMapping = entity?.quizzlerObject?.idMapping;

                                if (!ofIdMapping || !entityIdMapping) return false;

                                return Object.values(ofIdMapping).some(val => Object.values(entityIdMapping).includes(val));
                            });

                            const groupedFacts = entityFacts.reduce((acc, fact) => {
                                const cat = optionFactCategoryToDisplayCategory[fact.ofqo?.category] || 'Other';
                                if (!acc[cat]) acc[cat] = [];
                                acc[cat].push(fact);
                                return acc;
                            }, {});

                            const displayCategories = Object.keys(groupedFacts);

                            // AI ONLY: add AI tab at the end
                            displayCategories.push(AI_FACTS_TAB_LABEL);

                            const innerTab = innerTabs[entityIdx] || 0;

                            return (
                                <Box key={`entity-${entityIdx}`} sx={{ mt: 2 }}>
                                    <Tabs
                                        value={innerTab}
                                        onChange={(e, v) => handleInnerTabChange(entityIdx, v)}
                                        variant="scrollable"
                                        scrollButtons="auto"
                                        textColor="secondary"
                                        indicatorColor="secondary"
                                        sx={{
                                            minHeight: '32px',
                                            mt: 2,
                                            '& .MuiTab-root': {
                                                minHeight: '32px',
                                                fontSize: '0.85rem',
                                                textTransform: 'none',
                                                px: 1.5,
                                            },
                                            '& .Mui-selected': {
                                                fontWeight: 'bold',
                                                borderBottom: '2px solid',
                                                borderColor: 'secondary.main',
                                            },
                                            borderBottom: 1,
                                            borderColor: 'divider',
                                        }}
                                    >
                                        {displayCategories.map((label) => (
                                            <Tab key={label} label={label} disableRipple />
                                        ))}
                                    </Tabs>

                                    <Box sx={{ mt: 2 }}>
                                        {displayCategories[innerTab] === AI_FACTS_TAB_LABEL ? (
                                            <Box>

                                                {aiFactsByEntityIdx[entityIdx].map((f, i) => {



                                                    // AI ONLY: wrap into the same "fact" shape your flow expects
                                                    const wrappedFact = {
                                                        ofqo: {
                                                            messageValues: [f.text],
                                                            adminLabel: `Score: ${f.score01}`,
                                                            adminLabel2: f.explanation ? `Explanation: ${f.explanation}` : null,
                                                            selectedMessageTokenIndex: 0,
                                                            correctOption: {
                                                                qv: entity,
                                                            },
                                                            // optional: keep some provenance if you want it later
                                                            ai: {
                                                                index: f.index,
                                                                entityLabel: f.entityLabel,
                                                                entityWikidataId: f.entityWikidataId
                                                            }
                                                        }
                                                    };

                                                    return (
                                                        <React.Fragment key={'aifact' + i}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1.5 }}>
                                                                <Box sx={{ flexGrow: 1 }}>
                                                                    <Typography sx={{ lineHeight: 1.2, mb: 1 }}>
                                                                        {f.text}
                                                                    </Typography>
                                                                </Box>
                                                                <Button onClick={() => onSelectFact(wrappedFact)}>Select</Button>
                                                            </Box>

                                                            <Box sx={{ mt: -1 }}>
                                                                <Typography sx={{ opacity: 0.5, fontSize: '0.8em', flexGrow: 1 }}>
                                                                    {wrappedFact.ofqo.adminLabel}
                                                                </Typography>
                                                            </Box>

                                                            {wrappedFact.ofqo.adminLabel2 && (

                                                                <Box>
                                                                    <Typography sx={{ opacity: 0.5, fontSize: '0.8em', flexGrow: 1 }}>
                                                                        {wrappedFact.ofqo.adminLabel2}
                                                                    </Typography>
                                                                </Box>)
                                                            }
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </Box>
                                        ) : (

                                            groupedFacts[displayCategories[innerTab]]?.map((fact, i) => {
                                                const msg = fact.ofqo?.messageValues?.[0] || '';

                                                return (
                                                    <React.Fragment key={'fact' + i}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1.5 }}>
                                                            <Box sx={{ flexGrow: 1 }}>

                                                                <Typography sx={{ lineHeight: 1.2, mb: 1 }}>
                                                                    {msg}
                                                                </Typography>

                                                            </Box>
                                                            <Button onClick={() => onSelectFact(fact)}>Select</Button>
                                                        </Box>

                                                        <Box sx={{ mt: -1 }}>
                                                            <Typography sx={{ opacity: 0.5, fontSize: '0.8em', flexGrow: 1 }}>
                                                                {fact.ofqo?.adminLabel}
                                                            </Typography>
                                                        </Box>
                                                    </React.Fragment>
                                                );
                                            })
                                        )}
                                    </Box>
                                </Box>
                            );
                        })}
                    </>
                )}
            </Box>
        </Box >
    );
}
